import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';

import { useMerchCost } from '../hooks/useCostData';
import { RangeControls } from '../components/RangeControls';
import { VendorBreakdown } from '../components/VendorBreakdown';
import {
  EMPTY_CUSTOM_RANGE,
  INTERVAL_MS,
  bucketLabelFormat,
  formatUsd,
  intervalForSpan,
  resolveRange,
  type CustomRange,
  type IntervalKey,
  type RangeKey,
} from '../time';

const COLOR_MERCH = '#6366f1';
const COLOR_GENAPI = '#a855f7';
const COLOR_GCP = '#f59e0b';
const COLOR_TAGGERS = '#14b8a6';

interface BucketRow {
  label: string;
  merchGen: number;
  genApi: number;
  gcp: number;
  taggers: number;
}

/** Window used while a custom range is still incomplete — nothing is fetched. */
const NO_RANGE = { from: new Date(0), to: new Date(0) };

export function MerchCostPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const [custom, setCustom] = useState<CustomRange>(EMPTY_CUSTOM_RANGE);
  const [intervalKey, setIntervalKey] = useState<IntervalKey>(intervalForSpan(7 * 24 * 60 * 60_000));
  const [reloadKey, setReloadKey] = useState(0);

  const resolved = useMemo(
    () => resolveRange(rangeKey, custom),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey, custom, reloadKey],
  );
  const range = resolved ?? NO_RANGE;

  const {
    items,
    totals,
    gcpCostPerScan,
    taggersCostPerScan,
    genApiWarning,
    loading,
    error,
    saveGcp,
    saveTaggers,
  } = useMerchCost(range);

  // Local editor state for the configurable GCP flat cost per scan.
  const [gcpInput, setGcpInput] = useState<string>('');
  const [savingGcp, setSavingGcp] = useState(false);
  const [gcpError, setGcpError] = useState<string | null>(null);
  useEffect(() => {
    setGcpInput(String(gcpCostPerScan));
  }, [gcpCostPerScan]);

  // Local editor state for the configurable taggers flat cost per scan.
  const [taggersInput, setTaggersInput] = useState<string>('');
  const [savingTaggers, setSavingTaggers] = useState(false);
  const [taggersError, setTaggersError] = useState<string | null>(null);
  useEffect(() => {
    setTaggersInput(String(taggersCostPerScan));
  }, [taggersCostPerScan]);

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    const next = resolveRange(key, custom);
    if (next) setIntervalKey(intervalForSpan(next.to.getTime() - next.from.getTime()));
  }

  function selectCustom(next: CustomRange) {
    setCustom(next);
    const resolvedCustom = resolveRange('custom', next);
    if (resolvedCustom) {
      setIntervalKey(intervalForSpan(resolvedCustom.to.getTime() - resolvedCustom.from.getTime()));
    }
  }

  async function handleSaveGcp() {
    const value = parseFloat(gcpInput);
    if (Number.isNaN(value) || value < 0) {
      setGcpError('Enter a non-negative number');
      return;
    }
    setSavingGcp(true);
    setGcpError(null);
    try {
      await saveGcp(value);
    } catch (e) {
      setGcpError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingGcp(false);
    }
  }

  async function handleSaveTaggers() {
    const value = parseFloat(taggersInput);
    if (Number.isNaN(value) || value < 0) {
      setTaggersError('Enter a non-negative number');
      return;
    }
    setSavingTaggers(true);
    setTaggersError(null);
    try {
      await saveTaggers(value);
    } catch (e) {
      setTaggersError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingTaggers(false);
    }
  }

  const buckets = useMemo<BucketRow[]>(() => {
    const step = INTERVAL_MS[intervalKey];
    const end = range.to.getTime();
    const start = Math.floor(range.from.getTime() / step) * step;
    const labelFmt = bucketLabelFormat(step, end - range.from.getTime());

    const map = new Map<number, BucketRow>();
    for (let t = start; t <= end; t += step) {
      map.set(t, { label: format(new Date(t), labelFmt), merchGen: 0, genApi: 0, gcp: 0, taggers: 0 });
    }
    for (const it of items) {
      const bucket = Math.floor(it.t / step) * step;
      const entry = map.get(bucket);
      if (!entry) continue;
      entry.merchGen += it.merchGen;
      entry.genApi += it.genApi;
      entry.gcp += it.gcp;
      entry.taggers += it.taggers;
    }
    return Array.from(map.values());
  }, [items, range, intervalKey]);

  const hasCost = totals.grand > 0;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Merch Cost</h1>
        <p className="text-sm text-slate-500 mt-1">
          Total merch spend per scan: generation (8 angles) + correlated gen-api calls + GCP cloud cost + taggers cost.
        </p>
      </div>
      <RangeControls
        rangeKey={rangeKey}
        onRangeKeyChange={selectRange}
        custom={custom}
        onCustomChange={selectCustom}
        intervalKey={intervalKey}
        onIntervalKeyChange={setIntervalKey}
        range={resolved}
        onRefresh={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );

  // Flat per-scan rates, so they stay editable no matter which window is shown.
  const pricingEditors = (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white flex flex-wrap items-end gap-3">
      <div>
        <label className="text-xs font-medium text-slate-500 block mb-1" htmlFor="gcp-cost">
          GCP cloud cost per scan (USD)
        </label>
        <input
          id="gcp-cost"
          type="number"
          min="0"
          step="0.001"
          value={gcpInput}
          onChange={(e) => setGcpInput(e.target.value)}
          className="w-40 px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
      <button
        type="button"
        onClick={handleSaveGcp}
        disabled={savingGcp}
        className="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-600 disabled:opacity-60"
      >
        {savingGcp ? 'Saving…' : 'Save'}
      </button>

      <div className="ml-2 pl-4 border-l border-slate-200">
        <label className="text-xs font-medium text-slate-500 block mb-1" htmlFor="taggers-cost">
          Taggers cost per scan (USD)
        </label>
        <input
          id="taggers-cost"
          type="number"
          min="0"
          step="0.001"
          value={taggersInput}
          onChange={(e) => setTaggersInput(e.target.value)}
          className="w-40 px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
      <button
        type="button"
        onClick={handleSaveTaggers}
        disabled={savingTaggers}
        className="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-600 disabled:opacity-60"
      >
        {savingTaggers ? 'Saving…' : 'Save'}
      </button>

      <p className="text-[11px] text-slate-400 max-w-md">
        Flat estimates allocated to every completed merch scan (no GCP/taggers billing integration exists).
      </p>
      {gcpError && <p className="text-xs text-danger w-full">{gcpError}</p>}
      {taggersError && <p className="text-xs text-danger w-full">{taggersError}</p>}
    </div>
  );

  // A half-filled custom range covers no window. The totals still in state
  // belong to the previously selected range, so showing them here would put a
  // number next to a range it was not measured over. Prompt instead.
  if (!resolved) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        {header}
        <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 px-4 py-3 text-sm">
          Pick a start and end date to load costs for a custom period.
        </div>
        {pricingEditors}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {header}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load merch cost: {error}
        </div>
      )}
      {genApiWarning && (
        <div className="rounded-lg border border-amber-300/50 bg-amber-50 text-amber-700 px-4 py-3 text-sm">
          Gen-API cost unavailable — showing merch generation + GCP only. ({genApiWarning})
        </div>
      )}

      {pricingEditors}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        <StatCard label="Grand total" value={formatUsd(totals.grand)} sub="all components" />
        <StatCard label="Scans" value={String(totals.scans)} sub="completed merch scans" />
        <StatCard label="Avg / scan" value={formatUsd(totals.avgPerScan)} sub="grand total ÷ scans" />
        <StatCard label="Merch gen" value={formatUsd(totals.merchGen)} sub="8 angles" tone={COLOR_MERCH} />
        <StatCard label="Gen-API" value={formatUsd(totals.genApi)} sub="same job id" tone={COLOR_GENAPI} />
        <StatCard label="GCP" value={formatUsd(totals.gcp)} sub="cloud service" tone={COLOR_GCP} />
        <StatCard label="Taggers" value={formatUsd(totals.taggers)} sub="tagging labor" tone={COLOR_TAGGERS} />
      </div>

      {/* Cost over time (stacked by component) */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Cost over time</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{formatUsd(totals.grand)}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading merch cost…
            </div>
          ) : hasCost ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: number) => formatUsd(v)} />
                <Tooltip
                  formatter={(value: number, name: string) => [formatUsd(value), name]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
                />
                <Bar dataKey="merchGen" name="Merch generation" stackId="a" fill={COLOR_MERCH} />
                <Bar dataKey="genApi" name="Gen-API" stackId="a" fill={COLOR_GENAPI} />
                <Bar dataKey="gcp" name="GCP" stackId="a" fill={COLOR_GCP} />
                <Bar dataKey="taggers" name="Taggers" stackId="a" fill={COLOR_TAGGERS} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No merch cost in this period
            </div>
          )}
        </div>
      </div>

      {/* Vendor breakdown */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">By vendor</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Who the grand total is paid to: generation and gen-api spend attributed to the provider that
              billed it, plus GCP and taggers as their own lines.
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="text-lg font-bold text-slate-700">{totals.vendors.length}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">vendors</span>
          </div>
        </div>
        {loading && items.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">Loading vendor breakdown…</p>
        ) : (
          <VendorBreakdown
            vendors={totals.vendors}
            total={totals.grand}
            emptyLabel="No merch spend to attribute in this period."
          />
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
        {tone && <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: tone }} />}
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
